/*
 * Cassaforte — papelera: restaurar o eliminar definitivamente.
 * Usa las funciones originales de Psono (también borran los secretos en el servidor).
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import datastorePasswordService from "../../services/datastore-password";
import widgetService from "../../services/widget";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>;

export function collectTrash(node: AnyObj | undefined, out: AnyObj[]) {
	if (!node) return;
	(node.items || []).forEach((it: AnyObj) => { if (it.deleted && it.secret_id) out.push(it); });
	(node.folders || []).forEach((f: AnyObj) => collectTrash(f, out));
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const XIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg>;

async function freshDs(): Promise<AnyObj> {
	const ds = (await datastorePasswordService.getPasswordDatastore()) as unknown as AnyObj | undefined;
	if (!ds) throw new Error("no datastore");
	return ds;
}

export const TrashSheet = ({ items, onClose, onRestored, onRemoved, toast }: {
	items: AnyObj[]; onClose: () => void; onRestored: (it: AnyObj) => void; onRemoved: (ids: string[]) => void; toast: (m: string) => void;
}) => {
	const { t } = useTranslation();
	const [q, setQ] = useState("");
	const [busy, setBusy] = useState("");
	const [ask, setAsk] = useState<"" | "empty" | string>("");
	const list = useMemo(() => {
		const s = q.trim().toLowerCase();
		return s ? items.filter((it) => `${it.name || ""} ${it.description || ""} ${it.urlfilter || ""}`.toLowerCase().includes(s)) : items;
	}, [items, q]);

	const clean = (it: AnyObj) => { const c = { ...it }; delete c._path; return c; };

	const restore = async (it: AnyObj) => {
		setBusy(it.id);
		try {
			const ds = await freshDs();
			await widgetService.reverseMarkItemAsDeleted(ds as never, clean(it) as never, it.path, "password");
			onRestored(it); toast(t("CF_TRASH_RESTORED", { name: it.name }));
		} catch (e) { toast(t("CF_ERROR_SAVE")); }
		setBusy("");
	};
	const remove = async (it: AnyObj, quiet = false) => {
		const ds = await freshDs();
		await widgetService.deleteItemPermanent(ds as never, it.path, "password");
		if (quiet) { await wait(700); return; }
		onRemoved([it.id]); toast(t("CF_TRASH_DELETED", { name: it.name }));
	};
	const removeOne = async (it: AnyObj) => {
		setBusy(it.id); setAsk("");
		try { await remove(it); } catch (e) { toast(t("CF_ERROR_SAVE")); }
		setBusy("");
	};
	const emptyAll = async () => {
		setBusy("all"); setAsk("");
		try {
			const all = [...items];
			const done: string[] = [];
			try {
				for (const it of all) { await remove(it, true); done.push(it.id); }
				toast(t("CF_TRASH_EMPTIED"));
			} finally { onRemoved(done); }
		} catch (e) { toast(t("CF_ERROR_SAVE")); }
		setBusy("");
	};

	return (
		<div className="cf-scrim">
			<div className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfTrashTitle">
				<div className="cf-shead">
					<button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={onClose} disabled={busy === "all"}>{XIcon}</button>
					<h3 id="cfTrashTitle">{t("CF_TRASH_TITLE")}</h3>
					<p className="cf-lead">{t("CF_TRASH_LEAD")}</p>
				</div>
				<div className="cf-sbody">
					{items.length > 4 && <label className="cf-in"><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("CF_TRASH_SEARCH")} /></label>}
					{items.length === 0 && <div className="cf-empty" style={{ padding: "26px 10px" }}><h3>{t("CF_TRASH_EMPTY_TITLE")}</h3><p>{t("CF_TRASH_EMPTY_HINT")}</p></div>}
					<ul className="cf-trash">
						{list.map((it) => (
							<li key={it.id}>
								<div className="cf-trash-txt"><b>{it.name}</b><span>{it.description || it.urlfilter || ""}</span></div>
								{ask === it.id ? (
									<div className="cf-trash-act">
										<span className="cf-muted" style={{ fontSize: 13 }}>{t("CF_TRASH_SURE")}</span>
										<button type="button" className="cf-ghost" onClick={() => setAsk("")}>{t("CANCEL")}</button>
										<button type="button" className="cf-btn cf-danger cf-solid-danger" autoFocus onClick={() => removeOne(it)}>{t("CF_TRASH_CONFIRM_DELETE")}</button>
									</div>
								) : (
									<div className="cf-trash-act">
										<button type="button" className="cf-btn" disabled={!!busy} onClick={() => restore(it)}>{busy === it.id ? "…" : t("CF_TRASH_RESTORE")}</button>
										<button type="button" className="cf-btn cf-danger" disabled={!!busy} onClick={() => setAsk(it.id)}>{t("CF_TRASH_DELETE")}</button>
									</div>
								)}
							</li>))}
					</ul>
					{ask === "empty" && <div className="cf-inlineconfirm" role="alertdialog"><p><b>{t("CF_TRASH_EMPTY_Q", { count: items.length })}</b></p><p>{t("CF_TRASH_EMPTY_TXT")}</p>
						<div className="cf-row2"><button type="button" className="cf-ghost" onClick={() => setAsk("")}>{t("CANCEL")}</button><button type="button" className="cf-leave" autoFocus onClick={emptyAll}>{t("CF_TRASH_EMPTY_CONFIRM")}</button></div></div>}
				</div>
				<div className="cf-bar">
					{items.length > 0 && ask !== "empty" && <button type="button" className="cf-ghost cf-danger-text" disabled={!!busy} onClick={() => setAsk("empty")}>{busy === "all" ? t("CF_TRASH_EMPTYING") : t("CF_TRASH_EMPTY_BTN")}</button>}
					<button type="button" className="cf-save" disabled={busy === "all"} onClick={onClose}>{t("CF_DONE")}</button>
				</div>
			</div>
		</div>
	);
};
